/**
 * shapeRect.ts
 * Utilitaires pour calculer les rectangles et bounding boxes des shapes
 * Gère les tailles implicites (text, photoSpace, etc.)
 */
import type { ShapeData } from '../types/ShapeTypes';
import { rotateVec } from './geometry';

/**
 * Retourne les dimensions par défaut selon le type de shape
 */
export function getDefaultWH(shape: ShapeData) {
  switch (shape.type) {
    case 'text':
      return { w: 220, h: 60 };
    case 'image':
      return { w: 200, h: 150 };
    case 'circle':
      return { w: 100, h: 100 };
    case 'line':
      return { w: 120, h: 6 };
    case 'arrow':
      return { w: 120, h: 40 };
    case 'photoSpace':
      return { w: 240, h: 180 };
    default:
      return { w: 120, h: 80 };
  }
}

/**
 * Retourne le rectangle complet d'une shape (avec tailles implicites)
 */
export function getShapeRect(shape: ShapeData) {
  const { w, h } = getDefaultWH(shape);
  const width = shape.width ?? w;
  const height = shape.height ?? h;

  return {
    x: shape.x ?? 0,
    y: shape.y ?? 0,
    width,
    height,
    right: (shape.x ?? 0) + width,
    bottom: (shape.y ?? 0) + height,
    cx: (shape.x ?? 0) + width / 2,
    cy: (shape.y ?? 0) + height / 2,
  };
}

/**
 * Calcule la bounding box d'une sélection de shapes
 */
export function getSelectionBBox(shapes: ShapeData[], ids: string[]) {
  const selected = shapes.filter(s => ids.includes(s.id));
  if (!selected.length) return null;

  let minX = Infinity,
    minY = Infinity,
    maxX = -Infinity,
    maxY = -Infinity;

  for (const s of selected) {
    const r = getShapeRect(s);
    minX = Math.min(minX, r.x);
    minY = Math.min(minY, r.y);
    maxX = Math.max(maxX, r.right);
    maxY = Math.max(maxY, r.bottom);
  }

  return {
    x: minX,
    y: minY,
    width: maxX - minX,
    height: maxY - minY,
    right: maxX,
    bottom: maxY,
    cx: minX + (maxX - minX) / 2,
    cy: minY + (maxY - minY) / 2,
  };
}

/**
 * PR-13: Retourne width/height pour les opérations (gère les cas spéciaux)
 */
export function getShapeWHForOps(shape: ShapeData) {
  // Utilise width/height si présents (c'est souvent le cas chez toi)
  const { w, h } = getDefaultWH(shape);

  // Arrow : height pas utile → 0
  if (shape.type === 'arrow') {
    return { width: shape.width ?? 100, height: 0 };
  }

  // Text : souvent width/height existent dans ton store (TextToolService sync)
  if (shape.type === 'text') {
    const width = shape.width ?? w;
    const height = shape.height ?? h;
    return { width, height };
  }

  return {
    width: shape.width ?? w,
    height: shape.height ?? h,
  };
}

/**
 * PR-13: Centre MONDE (world center) d'une shape dont la rotation est appliquée
 * autour de son origin (top-left).
 * => center = origin + rotate((w/2,h/2), rotation)
 */
export function getShapeWorldCenter(shape: ShapeData) {
  const { width, height } = getShapeWHForOps(shape);
  const rot = shape.rotation ?? 0;
  const off = rotateVec(width / 2, height / 2, rot);
  return { x: (shape.x ?? 0) + off.x, y: (shape.y ?? 0) + off.y };
}

/**
 * PR-13: Recalcule origin (x,y) pour obtenir un centre monde donné, avec une rotation donnée.
 * => origin = center - rotate((w/2,h/2), rotation)
 */
export function originFromWorldCenter(
  centerX: number,
  centerY: number,
  width: number,
  height: number,
  rotationDeg: number
) {
  const off = rotateVec(width / 2, height / 2, rotationDeg);
  return { x: centerX - off.x, y: centerY - off.y };
}
